import { useCallback, useMemo } from 'react';
import {
  useGetHurricanesHistoricalQuery,
  useGetHurricanesLiveQuery,
} from 'services/weather/weather.api';
import { HURRICANES_QUERY_OPTIONS } from '../config/hurricanes.config';
import { groupStormRecords } from '../utils/hurricanes.utils';

// Fetches live and historical hurricane records, drops records that do not
// identify a storm, and combines both request states into one result for the UI.
export default function useHurricaneData() {
  const liveQuery = useGetHurricanesLiveQuery(undefined, HURRICANES_QUERY_OPTIONS);
  const historicalQuery = useGetHurricanesHistoricalQuery(undefined, HURRICANES_QUERY_OPTIONS);

  const liveStormGroups = useMemo(() => groupStormRecords(liveQuery.data), [liveQuery.data]);
  const historicalStormGroups = useMemo(
    () => groupStormRecords(historicalQuery.data),
    [historicalQuery.data],
  );

  const isLiveLoading = Boolean(liveQuery.isLoading);
  const isHistoricalLoading = Boolean(historicalQuery.isLoading);
  const liveError = Boolean(liveQuery.isError);
  const historicalError = Boolean(historicalQuery.isError);
  const hasStormData = liveStormGroups.length > 0 || historicalStormGroups.length > 0;
  const isLoading = !hasStormData && (isLiveLoading || isHistoricalLoading);
  const isError = !hasStormData && !isLoading && (liveError || historicalError);
  const isRefreshing =
    hasStormData && (Boolean(liveQuery.isFetching) || Boolean(historicalQuery.isFetching));

  const refetch = useCallback(() => {
    liveQuery.refetch();
    historicalQuery.refetch();
  }, [liveQuery.refetch, historicalQuery.refetch]);

  return {
    liveStormGroups,
    historicalStormGroups,
    hasLiveStorms: liveStormGroups.length > 0,
    isLiveLoading,
    isHistoricalLoading,
    isLoading,
    isRefreshing,
    liveError,
    historicalError,
    isError,
    refetch,
  };
}
